import React, { useRef, useEffect } from 'react';

interface CameraPreviewProps {
  stream: MediaStream | null;
  status?: string;
  className?: string;
}

export const CameraPreview: React.FC<CameraPreviewProps> = ({
  stream,
  status = 'active',
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const videoEl = videoRef.current;
    if (!videoEl) return;

    if (stream) {
      videoEl.srcObject = stream;
      videoEl.play().catch(() => {
        // Autoplay may be handled silently
      });
    } else {
      videoEl.srcObject = null;
    }
  }, [stream]);

  const hasActiveVideo = stream && stream.getVideoTracks().some((t) => t.readyState === 'live');

  return (
    <div
      aria-label="Camera live preview"
      style={{
        position: 'relative',
        overflow: 'hidden',
        borderRadius: '12px',
        border: '1px solid rgba(255,255,255,0.09)',
        background: '#020617',
        aspectRatio: '16 / 9',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {hasActiveVideo ? (
        <>
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            aria-label="Live user video preview"
            style={{
              height: '100%',
              width: '100%',
              objectFit: 'cover',
              transform: 'scaleX(-1)', // Mirror camera feed for natural UX
            }}
          />
          {/* LIVE badge */}
          <div
            style={{
              position: 'absolute',
              top: '10px',
              left: '10px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              borderRadius: '20px',
              background: 'rgba(15,18,30,0.80)',
              padding: '3px 10px',
              fontSize: '11px',
              fontWeight: 700,
              color: '#34D399',
              border: '1px solid rgba(52,211,153,0.20)',
              backdropFilter: 'blur(6px)',
            }}
          >
            <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: '#34D399', display: 'inline-block', animation: 'pulse 1.5s infinite' }} />
            LIVE FEED
          </div>
          {/* Resolution badge */}
          <div
            style={{
              position: 'absolute',
              bottom: '10px',
              right: '10px',
              borderRadius: '6px',
              background: 'rgba(15,18,30,0.80)',
              padding: '2px 8px',
              fontSize: '10px',
              fontFamily: 'monospace',
              color: '#94A3B8',
              backdropFilter: 'blur(6px)',
            }}
          >
            720p HD Ready
          </div>
        </>
      ) : (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '24px',
            textAlign: 'center',
            gap: '10px',
            color: '#64748B',
          }}
        >
          <div
            style={{
              width: '48px',
              height: '48px',
              borderRadius: '50%',
              background: 'rgba(30,41,59,0.80)',
              border: '1px solid rgba(255,255,255,0.08)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '22px',
            }}
          >
            📷
          </div>
          <div>
            <p style={{ margin: 0, fontSize: '12px', fontWeight: 600, color: '#94A3B8' }}>
              {status === 'checking' ? 'Connecting to Camera...' : 'Camera Stream Inactive'}
            </p>
            <p style={{ margin: '3px 0 0', fontSize: '11px', color: '#475569' }}>
              Allow browser camera access to preview feed.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};
